import { useEffect, useState } from 'react';
import adminApi from '@/lib/adminApi';

const AdminSupportMessages = () => {
  const [messages, setMessages] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const response = await adminApi.get(`/admin/contact-messages${showDeleted ? '?include_deleted=true' : ''}`);
      setMessages(response.data.messages || []);
      setCounts(response.data.counts || {});
      setSelectedIds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted]);

  const allVisibleSelected = messages.length > 0 && messages.every((message) => selectedIds.includes(message.id));

  const toggleSelection = (id, checked) => {
    setSelectedIds((current) => (checked ? Array.from(new Set([...current, id])) : current.filter((item) => item !== id)));
  };

  const toggleAllVisible = () => {
    setSelectedIds(allVisibleSelected ? [] : messages.map((message) => message.id).filter(Boolean));
  };

  const runSingleAction = async (id, action) => {
    if (action === 'delete' && !window.confirm('Move this support message to Deleted Messages?')) return;
    if (action === 'permanent' && !window.confirm('Permanently delete this support message? This cannot be undone.')) return;
    setBusy(true);
    try {
      if (action === 'restore') await adminApi.post(`/admin/contact-messages/${id}/restore`);
      else if (action === 'permanent') await adminApi.delete(`/admin/contact-messages/${id}/permanent`);
      else await adminApi.delete(`/admin/contact-messages/${id}`);
      await loadMessages();
    } finally {
      setBusy(false);
    }
  };

  const runBulkAction = async (action) => {
    if (selectedIds.length === 0) return;
    const confirmText =
      action === 'permanent_delete'
        ? `Permanently delete ${selectedIds.length} selected support message(s)? This cannot be undone.`
        : action === 'restore'
          ? `Undo delete for ${selectedIds.length} selected support message(s)?`
          : `Move ${selectedIds.length} selected support message(s) to Deleted Messages?`;
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    try {
      await adminApi.post('/admin/bulk/contact-messages', { ids: selectedIds, action });
      await loadMessages();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
        <h2 className="text-3xl font-bold text-gray-900">Support messages</h2>
        <p className="mt-3 text-gray-600">
          Messages from website and Android app support forms arrive here, with email alerts sent to admin.
        </p>
        <div className="mt-6 grid gap-3 sm:grid-cols-4">
          {['new', 'in_review', 'resolved', 'spam'].map((status) => (
            <div key={status} className="rounded-2xl bg-orange-50 px-4 py-3">
              <p className="text-2xl font-bold text-gray-900">{counts[status] || 0}</p>
              <p className="text-sm capitalize text-gray-600">{status.replace('_', ' ')}</p>
            </div>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <button type="button" onClick={() => setShowDeleted(false)} className={!showDeleted ? 'btn-primary' : 'btn-outline'}>
            Active Messages
          </button>
          <button type="button" onClick={() => setShowDeleted(true)} className={showDeleted ? 'btn-primary' : 'btn-outline'}>
            Deleted Messages
          </button>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl border border-orange-100 bg-orange-50/50 p-3">
          <button type="button" onClick={toggleAllVisible} className="btn-outline px-4 py-2 text-sm" disabled={messages.length === 0}>
            {allVisibleSelected ? 'Clear all visible' : 'Select all visible'}
          </button>
          {!showDeleted ? (
            <button
              type="button"
              onClick={() => runBulkAction('delete')}
              disabled={busy || selectedIds.length === 0}
              className="rounded-full border border-red-200 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              Delete selected
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => runBulkAction('restore')}
                disabled={busy || selectedIds.length === 0}
                className="rounded-full border border-emerald-200 px-4 py-2 text-sm font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
              >
                Undo selected
              </button>
              <button
                type="button"
                onClick={() => runBulkAction('permanent_delete')}
                disabled={busy || selectedIds.length === 0}
                className="rounded-full border border-red-200 px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                Permanent delete selected
              </button>
            </>
          )}
          <span className="text-sm font-semibold text-gray-500">{selectedIds.length} message(s) selected</span>
        </div>
      </section>

      <section className="overflow-hidden rounded-[2rem] border border-orange-100 bg-white shadow-lg">
        {loading ? (
          <div className="p-6 text-gray-500">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="p-6 text-gray-500">No support messages found.</div>
        ) : (
          <div className="divide-y divide-orange-100">
            {messages.map((message) => (
              <div key={message.id} className="p-5 sm:p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(message.id)}
                      onChange={(event) => toggleSelection(message.id, event.target.checked)}
                      className="mt-2 h-4 w-4 accent-orange-600"
                      aria-label="Select support message"
                    />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{message.subject || 'Support request'}</h3>
                      <p className="mt-2 text-sm text-gray-500">
                        {[message.name, message.email, message.phone].filter(Boolean).join(' | ')}
                      </p>
                      <p className="mt-1 text-xs text-gray-400">{message.created_at ? new Date(message.created_at).toLocaleString() : ''}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="w-fit rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
                      {message.status || 'new'}
                    </span>
                    {showDeleted ? (
                      <>
                        <button type="button" onClick={() => runSingleAction(message.id, 'restore')} className="rounded-full border border-emerald-200 px-3 py-1 text-xs font-bold text-emerald-700 hover:bg-emerald-50">
                          Undo
                        </button>
                        <button type="button" onClick={() => runSingleAction(message.id, 'permanent')} className="rounded-full border border-red-200 px-3 py-1 text-xs font-bold text-red-700 hover:bg-red-50">
                          Permanent delete
                        </button>
                      </>
                    ) : (
                      <button type="button" onClick={() => runSingleAction(message.id, 'delete')} className="rounded-full border border-red-200 px-3 py-1 text-xs font-bold text-red-600 hover:bg-red-50">
                        Delete
                      </button>
                    )}
                  </div>
                </div>
                {message.message && <p className="mt-4 whitespace-pre-line text-sm text-gray-700">{message.message}</p>}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminSupportMessages;
